import {
  Box,
  Button,
  Popover,
  SelectChangeEvent,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { IProductStage } from 'src/models/ecommerce';
import useEcommerce from 'src/services/useEcommerce';
import { reducerUpdateAdvanceSearch } from 'src/features/advanceSearchEcommerce';
import FloatingLabelButton from '../FloatingLabelButton';
import FilterProductStageList from './FilterProductStageList';
import { useDebounce } from 'src/hooks/useDebounce';
import { v4 as uuidv4 } from 'uuid';

export default function FilterProductStage() {
  const theme = useTheme();
  const { selectedProductStages } = useAppSelector(
    (state) => state.storeAdvancedSearchEcommerce
  );
  const [open, setOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const dispatch = useAppDispatch();

  const projectId = useAppSelector((state) => state.users.project.id);
  const advanceSearchEcommerce = useAppSelector(
    (state) => state.storeAdvancedSearchEcommerce
  );

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [search, setSearch] = useDebounce('');

  const { useGetProductStages, useGetEcommerceStreamCommentingToProduct } =
    useEcommerce();

  const [selectedProductStagesLocal, setSelectedProductStagesLocal] = useState<
    IProductStage[]
  >(selectedProductStages);

  const { data: productList, loading: loadingProducts } =
    useGetEcommerceStreamCommentingToProduct({
      skip: !open,
      variables: {
        projectId: projectId,
        stageId: selectedProductStagesLocal.map((item) => item.id)
      },
      fetchPolicy: 'network-only'
    });

  const { data: productStages, loading: loadingProductStage } =
    useGetProductStages({
      skip: !open,
      variables: {
        projectId: projectId,
        page,
        limit,
        name: search ? search : undefined
      },
      fetchPolicy: 'network-only'
    });

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const handleChangeLimit = (event: SelectChangeEvent<number>) => {
    setLimit(Number(event.target.value));
    setPage(1);
  };

  const handleApply = () => {
    if (!productList) return;
    const allSubBrand =
      productList.ecommerceProductManagement_getProductHierarchy.flatMap(
        (brand) => brand.subBrands
      );
    const allProducts =
      productList.ecommerceProductManagement_getProductHierarchy.flatMap(
        (brand) => brand.subBrands.flatMap((subBrand) => subBrand.products)
      );
    dispatch(
      reducerUpdateAdvanceSearch({
        ...advanceSearchEcommerce,
        selectedBrand:
          productList.ecommerceProductManagement_getProductHierarchy,
        selectedSubBrand: allSubBrand,
        selectedProductStages: selectedProductStagesLocal,
        selectedOriginalToPost: allProducts,
        replyToOriginalId:
          allProducts.length > 0
            ? allProducts.map((stream) => stream.originalId)
            : [uuidv4()]
      })
    );
    handleClose();
  };

  useEffect(() => {
    setSelectedProductStagesLocal(selectedProductStages);
  }, [selectedProductStages]);

  return (
    <>
      <Stack maxWidth="400px" width="100%" spacing={0.5}>
        <FloatingLabelButton
          chipList={selectedProductStages.map((item) => item.name)}
          label="Product Stage"
          onClick={handleOpen}
          open={open}
        />
      </Stack>
      <Popover
        open={open}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <Box sx={{ p: theme.spacing(2), maxWidth: '600px', width: '100%' }}>
          <Typography variant="h4">Product Stage</Typography>
          <FilterProductStageList
            data={
              productStages?.ecommerceProductManagement_getProductStages
                .result || []
            }
            loading={loadingProductStage}
            selectedProductStages={selectedProductStagesLocal}
            onSelectedProductStages={(products: IProductStage[]) => {
              setSelectedProductStagesLocal(products);
            }}
            handleFilter={setSearch}
            totalPages={
              productStages?.ecommerceProductManagement_getProductStages
                .totalPages
            }
            page={page}
            limit={limit}
            handleChangePage={(page) => setPage(page)}
            handleChangeLimit={handleChangeLimit}
          />
          <Stack alignItems="end">
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={loadingProducts || loadingProductStage}
            >
              Apply
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
